import OTPEntry from '@/components/OTPEntry';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { useCollaboratorAuth } from '@/context/CollaboratorAuthContext';
import LaunchTrustSignals from '@/components/LaunchTrustSignals';
import PolicyConsentChecklist, { emptyPolicyConsent, hasAcceptedAllPolicies } from '@/components/PolicyConsentChecklist';
import {
  collaboratorCategoryNames,
  defaultCollaboratorCategoryOptions,
  getCollaboratorSubcategoryOptions,
  mergeCollaboratorCategorySettings,
  toCollaboratorCategoryPayload,
} from '@/lib/collaboratorCategories';
import { API_BASE_URL as API } from '@/lib/apiBase';

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;
const MAX_PORTFOLIO_IMAGES = 3;
const MAX_PORTFOLIO_VIDEOS = 2;
const withFixedSlots = (values, count) => [...(Array.isArray(values) ? values : []), ...Array(count).fill('')].slice(0, count);

const readApiMessage = (err, fallback) => {
  const detail = err.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail?.message) return detail.message;
  return fallback;
};

const emptyProfile = {
  name: '',
  email: '',
  password: '',
  date_of_birth: '',
  signup_intent: 'hiring',
  mobile: '',
  organization_name: '',
  organization_type: 'Production House',
  organization_category: 'Production & Talent Services',
  organization_subcategories: ['Production House'],
  organization_subcategory_other: '',
  city: '',
  website: '',
  instagram: '',
  facebook: '',
  bio: '',
  image_url: '',
  portfolio_image_urls: ['', '', ''],
  portfolio_video_urls: ['', ''],
};

const PasswordToggleButton = ({ visible, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="absolute inset-y-0 right-3 flex items-center text-gray-500 hover:text-gray-700"
    aria-label={visible ? 'Hide password' : 'Show password'}
    title={visible ? 'Hide password' : 'Show password'}
  >
    {visible ? (
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M3 3l18 18" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M10.58 10.58A2 2 0 0012 14a2 2 0 001.42-.58" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M9.88 5.09A10.94 10.94 0 0112 4.91c5.05 0 8.27 4.14 9 5.09a13.16 13.16 0 01-3.06 3.37M6.1 6.1A13.23 13.23 0 003 12c.73.95 3.95 5.09 9 5.09a10.9 10.9 0 004.11-.79" />
      </svg>
    ) : (
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M2.46 12C3.73 7.94 7.52 5 12 5c4.48 0 8.27 2.94 9.54 7-1.27 4.06-5.06 7-9.54 7-4.48 0-8.27-2.94-9.54-7z" />
        <circle cx="12" cy="12" r="3" strokeWidth="1.8" />
      </svg>
    )}
  </button>
);

const CaptchaBox = ({ onVerify }) => {
  const [containerId] = useState(() => `collaborator-turnstile-${Math.random().toString(36).slice(2)}`);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return undefined;

    let cancelled = false;
    let widgetId = null;

    const renderTurnstile = () => {
      const container = document.getElementById(containerId);
      if (cancelled || !window.turnstile || !container) return;
      widgetId = window.turnstile.render(`#${containerId}`, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: (token) => onVerify(token),
        'expired-callback': () => onVerify(''),
        'error-callback': () => onVerify(''),
      });
      setLoaded(true);
    };

    if (!document.querySelector('script[data-turnstile-script]')) {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.defer = true;
      script.dataset.turnstileScript = 'true';
      script.onload = renderTurnstile;
      document.body.appendChild(script);
    } else {
      renderTurnstile();
    }

    return () => {
      cancelled = true;
      onVerify('');
      if (window.turnstile && widgetId !== null) {
        window.turnstile.remove(widgetId);
      }
    };
  }, [containerId, onVerify]);

  if (!TURNSTILE_SITE_KEY) {
    return <p className="text-xs text-red-600 mt-3">CAPTCHA is not configured yet.</p>;
  }

  return (
    <div className="mt-4 flex justify-center min-h-[70px]">
      <div id={containerId} />
      {!loaded && <span className="text-sm text-gray-500">Loading CAPTCHA...</span>}
    </div>
  );
};

const CollaboratorJoinModal = ({ isOpen, onClose, initialProof }) => {
  const navigate = useNavigate();
  const { setCollaborator, refreshCollaborator } = useCollaboratorAuth();
  const [flow, setFlow] = useState('otp');
  const [otpStep, setOtpStep] = useState(initialProof ? 3 : 1);
  const [verifiedProof, setVerifiedProof] = useState(initialProof || null);
  const [mobile, setMobile] = useState(initialProof?.mobile || '');
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [formData, setFormData] = useState(() => ({ ...emptyProfile, email: initialProof?.email || '', mobile: initialProof?.mobile || '' }));
  const [policyConsent, setPolicyConsent] = useState(emptyPolicyConsent);
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaKey, setCaptchaKey] = useState(0);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [collaboratorCategoryMap, setCollaboratorCategoryMap] = useState(defaultCollaboratorCategoryOptions);

  useEffect(() => {
    if (isOpen) return;
    setFlow('otp');
    setOtpStep(1);
    setMobile('');
    setLoginEmail('');
    setLoginPassword('');
    setFormData(emptyProfile);
    setPolicyConsent(emptyPolicyConsent);
    setCaptchaToken('');
    setCaptchaKey((value) => value + 1);
    setError('');
    setStatusMessage('');
    setLoading(false);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    let mounted = true;
    const loadCategories = async () => {
      try {
        const response = await axios.get(`${API}/settings/collaborator-categories`);
        if (!mounted) return;
        setCollaboratorCategoryMap(mergeCollaboratorCategorySettings(response.data?.settings));
      } catch (error) {
        if (mounted) setCollaboratorCategoryMap(defaultCollaboratorCategoryOptions);
      }
    };
    loadCategories();
    return () => {
      mounted = false;
    };
  }, [isOpen]);

  const resetCaptcha = () => {
    setCaptchaToken('');
    setCaptchaKey((value) => value + 1);
  };

  const completeAuth = async (collaboratorData) => {
    try {
      await axios.post(`${API}/artist-auth/logout`, {}, { withCredentials: true });
    } catch (logoutError) {
      // Ignore stale artist session cleanup failures.
    }
    setCollaborator(collaboratorData);
    const refreshedCollaborator = await refreshCollaborator();
    if (!refreshedCollaborator) {
      setCollaborator(collaboratorData);
    }
    onClose();
    navigate('/collaborator/account', { replace: true });
  };

  const requireCaptcha = () => {
    if (!TURNSTILE_SITE_KEY) {
      setError('CAPTCHA is not configured yet.');
      return false;
    }
    if (!captchaToken) {
      setError('Please complete the CAPTCHA verification');
      return false;
    }
    return true;
  };

  const handleEmailLogin = async (event) => {
    event.preventDefault();
    if (!requireCaptcha()) return;
    setLoading(true);
    setError('');
    setStatusMessage('');

    try {
      const response = await axios.post(
        `${API}/collaborator-auth/email-login`,
        { email: loginEmail, password: loginPassword, turnstile_token: captchaToken },
        { withCredentials: true }
      );
      await completeAuth(response.data);
    } catch (err) {
      setError(readApiMessage(err, 'Unable to sign in.'));
      resetCaptcha();
    } finally {
      setLoading(false);
    }
  };

  const buildRegistrationPayload = () => ({
    ...formData,
    ...toCollaboratorCategoryPayload(formData),
    mobile: formData.mobile.trim() || undefined,
    portfolio_video_urls: formData.portfolio_video_urls.map((value) => value.trim()).filter(Boolean),
    privacy_policy_accepted: Boolean(policyConsent.privacy),
    terms_accepted: Boolean(policyConsent.terms),
    trust_safety_accepted: Boolean(policyConsent.trust),
    turnstile_token: captchaToken,
  });

  const organizationSubcategoryOptions = getCollaboratorSubcategoryOptions(
    collaboratorCategoryMap,
    formData.organization_category
  );

  const handleCategoryChange = (mainCategory) => {
    const firstSubcategory = getCollaboratorSubcategoryOptions(collaboratorCategoryMap, mainCategory)[0] || '';
    setFormData((current) => ({
      ...current,
      organization_category: mainCategory,
      organization_subcategories: firstSubcategory ? [firstSubcategory] : [],
      organization_subcategory_other: '',
      organization_type: firstSubcategory || mainCategory,
    }));
  };

  const toggleSubcategory = (subcategory) => {
    setFormData((current) => {
      const selected = Array.isArray(current.organization_subcategories) ? current.organization_subcategories : [];
      const exists = selected.some((item) => item.toLowerCase() === subcategory.toLowerCase());
      const nextSubcategories = exists
        ? selected.filter((item) => item.toLowerCase() !== subcategory.toLowerCase())
        : [...selected, subcategory];
      return {
        ...current,
        organization_subcategories: nextSubcategories,
        organization_type: toCollaboratorCategoryPayload({
          ...current,
          organization_subcategories: nextSubcategories,
        }).organization_type,
      };
    });
  };

  const handleOtpRegister = async (event) => {
    event.preventDefault();
    if (!hasAcceptedAllPolicies(policyConsent)) {
      setError('Please read and accept the Privacy Policy, Terms & Conditions, and Trust & Safety rules before signup.');
      return;
    }
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(
        `${API}/collaborators/register`,
        { ...buildRegistrationPayload(), mobile, verified_mobile_token: verifiedProof?.verified_mobile_token, turnstile_token: undefined },
        { withCredentials: true }
      );
      await completeAuth(response.data.collaborator);
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploadingImage(true);
    setError('');

    try {
      const payload = new FormData();
      payload.append('file', file);
      const response = await axios.post(`${API}/uploads/profile-image`, payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
      });
      setFormData((current) => ({ ...current, image_url: response.data.image_url }));
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
      event.target.value = '';
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
  };

  const handleSlotChange = (field, index, value) => {
    setFormData((current) => {
      const limit = field === 'portfolio_image_urls' ? MAX_PORTFOLIO_IMAGES : MAX_PORTFOLIO_VIDEOS;
      const nextValues = [...withFixedSlots(current[field], limit)];
      nextValues[index] = value;
      return { ...current, [field]: nextValues };
    });
  };

  const switchFlow = (nextFlow) => {
    setFlow(nextFlow);
    setError('');
    resetCaptcha();
  };

  const updatePolicyConsent = (key, checked) => {
    setPolicyConsent((current) => ({ ...current, [key]: checked }));
    setError('');
  };

  if (!isOpen) return null;

  const renderProfileForm = () => (
    <>
      <input name="name" value={formData.name} onChange={handleChange} required placeholder="Your name" className="w-full px-4 py-3 border border-gray-300 rounded-lg" />
      <input name="email" type="email" value={formData.email} onChange={handleChange} required placeholder="Email address" className="w-full px-4 py-3 border border-gray-300 rounded-lg" />

      <div>
        <label className="mb-1 block text-sm font-semibold text-gray-700">Date of birth *</label>
        <input name="date_of_birth" type="date" value={formData.date_of_birth} onChange={handleChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg" />
        <p className="mt-1 text-xs text-gray-500">Required for age safety. Under-18 learner consent is planned for a later Learn phase.</p>
      </div>
      <input name="mobile" value={mobile} readOnly placeholder="Mobile number" className="w-full px-4 py-3 border border-gray-300 rounded-lg" />
      <input name="organization_name" value={formData.organization_name} onChange={handleChange} required placeholder="Organization / brand name" className="w-full px-4 py-3 border border-gray-300 rounded-lg" />
      <select
        name="organization_category"
        value={formData.organization_category}
        onChange={(event) => handleCategoryChange(event.target.value)}
        className="w-full px-4 py-3 border border-gray-300 rounded-lg"
      >
        {collaboratorCategoryNames.map((type) => <option key={type} value={type}>{type}</option>)}
      </select>
      {organizationSubcategoryOptions.length > 0 && (
        <div className="rounded-2xl border border-gray-200 p-3">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#4A148C] mb-2">Sub-categories</p>
          <div className="flex flex-wrap gap-2">
            {organizationSubcategoryOptions.map((subcategory) => {
              const selected = (formData.organization_subcategories || []).includes(subcategory);
              return (
                <button
                  key={subcategory}
                  type="button"
                  onClick={() => toggleSubcategory(subcategory)}
                  className={`rounded-full border px-3 py-1 text-sm transition ${
                    selected
                      ? 'border-[#4A148C] bg-[#F3E8FF] text-[#4A148C]'
                      : 'border-gray-200 bg-white text-gray-700'
                  }`}
                >
                  {subcategory}
                </button>
              );
            })}
          </div>
        </div>
      )}
      <input
        name="organization_subcategory_other"
        value={formData.organization_subcategory_other}
        onChange={handleChange}
        placeholder="Other sub-category (optional)"
        className="w-full px-4 py-3 border border-gray-300 rounded-lg"
      />
      <input name="city" value={formData.city} onChange={handleChange} placeholder="City / base location" className="w-full px-4 py-3 border border-gray-300 rounded-lg" />
      <input name="website" value={formData.website} onChange={handleChange} placeholder="Website URL" className="w-full px-4 py-3 border border-gray-300 rounded-lg" />
      <input name="instagram" value={formData.instagram} onChange={handleChange} placeholder="Instagram profile URL or username" className="w-full px-4 py-3 border border-gray-300 rounded-lg" />
      <input name="facebook" value={formData.facebook} onChange={handleChange} placeholder="Facebook page URL" className="w-full px-4 py-3 border border-gray-300 rounded-lg" />
      <p className="text-xs text-gray-500 -mt-2">
        Website or Instagram is optional, but helps artists trust the enquiry source.
        <span className="block mt-1">Website या Instagram optional है, लेकिन कलाकारों को आपकी enquiry पर भरोसा करने में मदद करता है।</span>
      </p>
      <div>
        <input name="image_url" value={formData.image_url} onChange={handleChange} placeholder="Profile image URL" className="w-full px-4 py-3 border border-gray-300 rounded-lg mb-2" />
        <input type="file" accept="image/*" onChange={handleImageUpload} className="w-full text-sm" />
        {uploadingImage && <p className="text-sm text-[#00BFA5] mt-1">Uploading image...</p>}
      </div>
      <div className="md:col-span-2 rounded-xl border bg-gray-50 p-4"><p className="font-semibold">Public photo gallery</p><p className="mt-1 text-sm text-gray-600">Additional profile photos are managed by KalaManch admin and shown on your public profile. You can still change your main profile photo.</p></div>
      <div className="rounded-xl border border-purple-100 bg-purple-50/60 p-3">
        <p className="text-sm font-semibold text-gray-800">Portfolio videos</p>
        <p className="mt-1 text-xs text-gray-500">Add up to {MAX_PORTFOLIO_VIDEOS} YouTube links only.</p>
        <div className="mt-3 space-y-2">
          {formData.portfolio_video_urls.map((url, index) => (
            <input key={`collab-join-video-${index}`} value={url} onChange={(event) => handleSlotChange('portfolio_video_urls', index, event.target.value)} placeholder={`YouTube video ${index + 1} URL`} className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white" />
          ))}
        </div>
      </div>
      <textarea name="bio" value={formData.bio} onChange={handleChange} rows="4" placeholder="Tell artists what kind of projects you create or hire for" className="w-full px-4 py-3 border border-gray-300 rounded-lg" />
    </>
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 px-3 py-4 sm:p-6 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[calc(100dvh-2rem)] sm:max-h-[calc(100vh-3rem)] overflow-y-auto my-auto">
        <div className="p-4 sm:p-6">
          <div className="flex justify-between items-center gap-4 mb-5">
            <h2 className="text-2xl font-bold gradient-text">Collaborator Sign In / Join</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700" aria-label="Close">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="mb-4 rounded-2xl border border-teal-100 bg-teal-50 px-4 py-3 text-sm text-gray-700">
            Existing accounts: sign in with your registered email. Add and verify your mobile number from your account after signing in.
          </div>
          <div className="mb-4">
            <LaunchTrustSignals compact tone="teal" />
          </div>

          <div className="mb-4 rounded-2xl sm:rounded-full bg-gray-100 p-1">
            <button type="button" onClick={() => switchFlow('otp')} className="btn-primary w-full mb-2">Email / WhatsApp code</button>
            <button type="button" onClick={() => switchFlow('email')} className="w-full rounded-2xl sm:rounded-full bg-white px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold text-[#FF6F00] shadow">Email + Password</button>
          </div>

          {error && <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">{error}</div>}
          {statusMessage && <div className="bg-green-100 border border-green-300 text-green-800 px-4 py-3 rounded mb-4">{statusMessage}</div>}

          {flow === 'email' && (
            <form onSubmit={handleEmailLogin} className="space-y-4">
        <input type="email" value={loginEmail} onChange={(event) => setLoginEmail(event.target.value)} required placeholder="Email address" className="w-full px-4 py-3 border border-gray-300 rounded-lg" />
        <div className="relative">
          <input type={showLoginPassword ? 'text' : 'password'} value={loginPassword} onChange={(event) => setLoginPassword(event.target.value)} required placeholder="Password" className="w-full px-4 py-3 pr-20 border border-gray-300 rounded-lg" />
          <PasswordToggleButton visible={showLoginPassword} onClick={() => setShowLoginPassword((prev) => !prev)} />
        </div>
        <div className="flex justify-end">
          <Link
            to={`/forgot-password?account=collaborator&email=${encodeURIComponent(loginEmail.trim())}`}
            onClick={onClose}
            className="text-sm font-semibold text-[#FF6F00] hover:text-[#E66300]"
          >
            Forgot Password?
          </Link>
        </div>
        <CaptchaBox key={`collab-login-${captchaKey}`} onVerify={setCaptchaToken} />
        <button type="submit" disabled={loading || !TURNSTILE_SITE_KEY || !captchaToken} className="btn-primary w-full">{loading ? 'Signing in...' : 'Sign In'}</button>
      </form>
          )}

          {flow === 'otp' && otpStep === 1 && <OTPEntry onProof={(proof, selectedRole) => {
            if (selectedRole !== 'collaborator') { onClose(); navigate('/sign-in', { state: { proof, selectedRole } }); return; }
            setVerifiedProof(proof); setMobile(proof.mobile);
            setFormData(current => ({ ...current, mobile: proof.mobile, email: proof.email || current.email }));
            setOtpStep(3);
          }} />}

          {flow === 'otp' && otpStep === 3 && (
            <form onSubmit={handleOtpRegister} className="space-y-4">
              <p className="rounded-xl bg-gray-50 px-4 py-3 text-sm text-gray-600">
                Complete your collaborator profile so artists can understand who is hiring and what kind of work you post.
                <span className="block mt-2 text-xs leading-5">Profile पूरी करें ताकि कलाकार समझ सकें कि कौन hiring कर रहा है और किस तरह का काम है।</span>
              </p>
              {renderProfileForm()}
              <PolicyConsentChecklist value={policyConsent} onChange={updatePolicyConsent} tone="teal" />
              <button type="submit" disabled={loading || !hasAcceptedAllPolicies(policyConsent)} className="btn-primary w-full">{loading ? 'Saving...' : 'Complete Registration'}</button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default CollaboratorJoinModal;
